import { OrdersModel } from './orders.model';
import { OrderListItem, OrdersSummary } from './orders.types';
import { ProductsModel } from '../products/products.model';

export class OrdersController {
  private setOrders: (orders: OrderListItem[]) => void;
  private setFilteredOrders: (orders: OrderListItem[]) => void;
  private setSummary: (summary: OrdersSummary) => void;
  private setLoading: (loading: boolean) => void;
  private setError: (error: string | null) => void;

  constructor(
    setOrders: (orders: OrderListItem[]) => void,
    setFilteredOrders: (orders: OrderListItem[]) => void,
    setSummary: (summary: OrdersSummary) => void,
    setLoading: (loading: boolean) => void,
    setError: (error: string | null) => void
  ) {
    this.setOrders = setOrders;
    this.setFilteredOrders = setFilteredOrders;
    this.setSummary = setSummary;
    this.setLoading = setLoading;
    this.setError = setError;
  }

  /**
   * Loads or reloads all order list records.
   */
  public async load(): Promise<void> {
    this.setLoading(true);
    this.setError(null);
    try {
      const data = await OrdersModel.fetchOrders();
      this.setOrders(data);
      
      // Default list hides finished orders
      const activeOnly = data.filter(order => order.statusName.toLowerCase() !== 'finalizado');
      this.setFilteredOrders(activeOnly);
      
      const summAttr = OrdersModel.calculateSummary(data);
      this.setSummary(summAttr);
    } catch (e: any) {
      this.setError(e.message || 'Failed loading active floor orders sequence.');
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Action handler for searching/filtering based on the native HTML form submit.
   * Resolves searching without using reactive state listeners (Strict compliance to instruction 1).
   * Also hides Finalizado orders unless user searches specifically for "finalizado" or "finalizados".
   */
  public filter(formData: FormData, originalOrders: OrderListItem[]): void {
    const searchVal = (formData.get('search') as string || '').toLowerCase().trim();
    
    if (!searchVal) {
      // Hide finalized orders when normal browsing
      const activeOnly = originalOrders.filter(order => order.statusName.toLowerCase() !== 'finalizado');
      this.setFilteredOrders(activeOnly);
      return;
    }

    const includesFinalizado = searchVal.includes('finalizado') || searchVal.includes('concluido') || searchVal.includes('entregue');

    const filtered = originalOrders.filter(order => {
      // If user is searching specifically for finalized, return finalized.
      // Otherwise, exclude finalized from general text search unless it matched number/city.
      if (order.statusName.toLowerCase() === 'finalizado' && !includesFinalizado) {
        return false;
      }

      return (
        order.number.toString().includes(searchVal) ||
        order.customerName.toLowerCase().includes(searchVal) ||
        order.city.toLowerCase().includes(searchVal) ||
        order.statusName.toLowerCase().includes(searchVal) ||
        (order.customStatus || '').toLowerCase().includes(searchVal)
      );
    });

    this.setFilteredOrders(filtered);
  }

  /**
   * Adds an order to the active list from new form data entries.
   */
  public async addOrder(formData: FormData, activeList: OrderListItem[]): Promise<boolean> {
    try {
      const number = ((formData.get('orderNumber') as string) || '').trim() || String(Math.floor(Math.random() * 1000));
      const customerName = formData.get('customerName') as string || 'Cliente Desconhecido';
      const city = formData.get('city') as string || 'Paraguaçu Paulista';
      const isPriority = formData.get('priority') === 'true' || formData.get('priority') === 'on';
      const isEmergence = formData.get('emergency') === 'true' || formData.get('emergency') === 'on';
      const customStatus = formData.get('customStatus') as string || '';
      const statusName = 'Aguardando';

      let departure = new Date().toISOString().split('T')[0];
      const departureVal = formData.get('departure') as string || '';
      if (departureVal) {
        departure = departureVal;
      }

      const allDbProducts = ProductsModel.getProducts();
      const listItemsStr = formData.get('itemNames') as string || '';
      const items = listItemsStr.split(',').filter(x => x.trim()).map((name, index) => {
        const matched = allDbProducts.find(p => p.name.trim().toLowerCase() === name.trim().toLowerCase());
        const productId = matched ? parseInt(matched.id, 10) : 1;
        return {
          productId: isNaN(productId) ? 1 : productId,
          productName: name.trim(),
          isProduced: false,
          status: 'Aguardando',
          colorHex: '#198754'
        };
      });

      const newOrder: OrderListItem = {
        id: Date.now(),
        number,
        customerName,
        departure,
        city,
        isPriority,
        isEmergence,
        createdAt: new Date().toISOString().split('T')[0],
        dayOfWeek: 'Segunda-Feira',
        statusName,
        customStatus,
        items
      };

      const success = await OrdersModel.saveOrderToServer(newOrder);
      if (success) {
        await this.load();
        return true;
      } else {
        this.setError("Could not save order on MySQL server.");
        return false;
      }
    } catch (e) {
      this.setError("Could not parse proposed FormData parameters.");
      return false;
    }
  }

  /**
   * Updates an existing order in the list with edited form data.
   */
  public async updateOrder(id: number, formData: FormData, activeList: OrderListItem[]): Promise<boolean> {
    try {
      const number = ((formData.get('orderNumber') as string) || '').trim() || String(Math.floor(Math.random() * 1000));
      const customerName = formData.get('customerName') as string || 'Cliente Desconhecido';
      const city = formData.get('city') as string || 'Paraguaçu Paulista';
      const isPriority = formData.get('priority') === 'true' || formData.get('priority') === 'on';
      const isEmergence = formData.get('emergency') === 'true' || formData.get('emergency') === 'on';
      const customStatus = formData.get('customStatus') as string || '';

      let departure = '';
      const departureVal = formData.get('departure') as string || '';
      if (departureVal) {
        departure = departureVal;
      }
      
      const originalOrder = activeList.find(o => o.id === id);
      if (!originalOrder) {
        this.setError("Order not found in memory.");
        return false;
      }

      const allDbProducts = ProductsModel.getProducts();
      const listItemsStr = formData.get('itemNames') as string || '';
      const itemNames = listItemsStr.split(',').filter(x => x.trim());
      
      const items = itemNames.map((name, index) => {
        const originalItem = originalOrder.items && originalOrder.items[index];
        const matched = allDbProducts.find(p => p.name.trim().toLowerCase() === name.trim().toLowerCase());
        const productId = matched ? parseInt(matched.id, 10) : 1;

        if (originalItem) {
          return {
            id: originalItem.id,
            productId: productId,
            productName: name.trim(),
            isProduced: originalItem.isProduced,
            status: originalItem.status,
            colorHex: originalItem.colorHex,
            collaboratorName: originalItem.collaboratorName,
            setor: originalItem.setor,
            periods: originalItem.periods || []
          };
        } else {
          return {
            productId: isNaN(productId) ? 1 : productId,
            productName: name.trim(),
            isProduced: false,
            status: 'Aguardando',
            colorHex: isEmergence ? '#dc3545' : '#198754',
            periods: []
          };
        }
      });

      const orderToUpdate: OrderListItem = {
        ...originalOrder,
        number,
        customerName,
        city,
        isPriority,
        isEmergence,
        departure: departure || originalOrder.departure,
        items: items.length > 0 ? items : originalOrder.items,
        statusName: isEmergence ? 'Aguardando' : originalOrder.statusName,
        customStatus
      };

      const success = await OrdersModel.updateOrderOnServer(orderToUpdate);
      if (success) {
        await this.load();
        return true;
      } else {
        this.setError("Could not save updated order on MySQL server.");
        return false;
      }
    } catch (e) {
      this.setError("Could not update order with proposed parameters.");
      return false;
    }
  }

  /**
   * Deletes an order from the active list.
   */
  public async deleteOrder(id: number, activeList: OrderListItem[]): Promise<void> {
    try {
      const success = await OrdersModel.deleteOrderFromServer(id);
      if (success) {
        await this.load();
      } else {
        this.setError("Failed deleting order on MySQL server.");
      }
    } catch (e) {
      this.setError("Error trying to contact servers for order removal.");
    }
  }
}
